import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { APIS } from "../../../utils/APIS";
import { GET, POST, PUT, DELETE } from "../../../utils/Methods";
import { IDS } from "../../../utils/IDS";

// --- GET ALL CARDS ---
export const GetDonationCards = createAsyncThunk(
  "donationcard/get-all",
  async (filter, { rejectWithValue }) => {
    try {
      const res = await GET(
        `${APIS.Donation.Donation_Card}?name=${filter?.name || ""}&page=${filter?.page || IDS.PAGE}&per_page=${filter?.per_page || IDS.PERPAGE}`
      );
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- GET SINGLE CARD ---
export const GetDonationCardById = createAsyncThunk(
  "donationcard/get-single",
  async (id, { rejectWithValue }) => {
    try {
      const res = await GET(`${APIS.Donation.Donation_Card}/${id}`);
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- CREATE CARD ---
export const CreateDonationCard = createAsyncThunk(
  "donationcard/create",
  async (data, { rejectWithValue }) => {
    try {
      const res = await POST(APIS.Donation.Donation_Card, data);
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- UPDATE CARD ---
export const UpdateDonationCard = createAsyncThunk(
  "donationcard/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await PUT(`${APIS.Donation.Donation_Card}/${id}`, data);
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- DELETE CARD ---
export const DeleteDonationCard = createAsyncThunk(
  "donationcard/delete",
  async (id, { rejectWithValue }) => {
    try {
      await DELETE(`${APIS.Donation.Donation_Card}/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- TOGGLE CARD STATUS ---
export const ToggleDonationCardStatus = createAsyncThunk(
  "donationcard/status",
  async (id, { rejectWithValue }) => {
    try {
      const res = await POST(`${APIS.Donation.Donation_Card}/${id}/status`);
      return { id, ...res };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
// --- GET DROPDOWN CARDS ---
export const GetDropdownCards = createAsyncThunk(
  "donationcard/get-dropdown",
  async (_, { rejectWithValue }) => {
    try {
      const res = await GET(`${APIS.Donation.Donation_Card}?dropdown=true`); 
      // backend should return only {id, name} for dropdown
      return res.data; 
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- ADD PLANS ---
export const AddPlans = createAsyncThunk(
  "donationcard/add-plans",
  async ({ cardId, data }, { rejectWithValue }) => {
    try {
      const res = await POST(`${APIS.Donation.Donation_Card}/${cardId}/plans`, data);
      return res.data; 
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


// --- SLICE ---
const Slice = createSlice({
  name: "donationcard",
  initialState: {
    data: [], 
    dropdown:[],            // List of cards
    current_page: IDS.PAGE,
    total_pages: IDS.TOTALPAGES,
    per_page: IDS.PERPAGE,
    total: IDS.TOTAL,
    status: IDS.SLICESTATUS.Idle,
    error: null,
    singleCard: null,     // For edit modal
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // --- GET ALL ---
      .addCase(GetDonationCards.pending, (state) => { state.status = IDS.SLICESTATUS.Loading; })
      .addCase(GetDonationCards.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        state.data = action.payload?.data?.data || [];
        state.current_page = action.payload?.data?.current_page || IDS.PAGE;
        state.total_pages = action.payload?.data?.total_pages || IDS.TOTALPAGES;
        state.per_page = action.payload?.data?.per_page || IDS.PERPAGE;
        state.total = action.payload?.data?.total || IDS.TOTAL;
      })
      .addCase(GetDonationCards.rejected, (state, action) => { state.status = IDS.SLICESTATUS.Failed; state.error = action.payload; })

      // --- GET SINGLE ---
      .addCase(GetDonationCardById.pending, (state) => { state.status = IDS.SLICESTATUS.Loading; })
      .addCase(GetDonationCardById.fulfilled, (state, action) => { state.status = IDS.SLICESTATUS.Succeeded; state.singleCard = action.payload?.data; })
      .addCase(GetDonationCardById.rejected, (state, action) => { state.status = IDS.SLICESTATUS.Failed; state.error = action.payload; })

      // --- CREATE ---
      .addCase(CreateDonationCard.pending, (state) => { state.status = IDS.SLICESTATUS.Loading; })
      .addCase(CreateDonationCard.fulfilled, (state) => { state.status = IDS.SLICESTATUS.Succeeded; })
      .addCase(CreateDonationCard.rejected, (state, action) => { state.status = IDS.SLICESTATUS.Failed; state.error = action.payload; })

      // --- UPDATE ---
      .addCase(UpdateDonationCard.pending, (state) => { state.status = IDS.SLICESTATUS.Loading; })
      .addCase(UpdateDonationCard.fulfilled, (state) => { state.status = IDS.SLICESTATUS.Succeeded; })
      .addCase(UpdateDonationCard.rejected, (state, action) => { state.status = IDS.SLICESTATUS.Failed; state.error = action.payload; })

      // --- DELETE ---
      .addCase(DeleteDonationCard.pending, (state) => { state.status = IDS.SLICESTATUS.Loading; })
      .addCase(DeleteDonationCard.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        state.data = state.data.filter((card) => card.id !== action.payload);
      })
      .addCase(DeleteDonationCard.rejected, (state, action) => { state.status = IDS.SLICESTATUS.Failed; state.error = action.payload; })

      // --- STATUS ---
      .addCase(ToggleDonationCardStatus.pending, (state) => { state.status = IDS.SLICESTATUS.Loading; })
      .addCase(ToggleDonationCardStatus.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        const id = action.payload.id;
        const index = state.data.findIndex((card) => card.id === id);
        if (index !== -1) state.data[index].status = !state.data[index].status;
      })
      .addCase(ToggleDonationCardStatus.rejected, (state, action) => { state.status = IDS.SLICESTATUS.Failed; state.error = action.payload; })
.addCase(GetDropdownCards.pending, (state) => { state.status = IDS.SLICESTATUS.Loading; })
.addCase(GetDropdownCards.fulfilled, (state, action) => {
  state.status = IDS.SLICESTATUS.Succeeded;
  state.dropdown = action.payload || [];
})
.addCase(GetDropdownCards.rejected, (state, action) => { state.status = IDS.SLICESTATUS.Failed; state.error = action.payload; })
.addCase(AddPlans.pending, (state) => { state.status = IDS.SLICESTATUS.Loading; })
.addCase(AddPlans.fulfilled, (state) => { state.status = IDS.SLICESTATUS.Succeeded; })
.addCase(AddPlans.rejected, (state, action) => { state.status = IDS.SLICESTATUS.Failed; state.error = action.payload; })

  }
});

export default Slice.reducer;
